import {
  DestroyRef,
  EnvironmentProviders,
  inject,
  Injectable,
  provideAppInitializer,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { filter, take, timer } from 'rxjs';

import { carriesPreloadFor } from './app-loading.config';
import { SupportedLanguage } from './app.i18n';
import { AuthActions } from './auth/store/auth.actions';
import { AssistantsActions } from './chats/assistants/store/assistants.actions';
import { ChatsActions } from './chats/store/chats.actions';
import { TeammatesActions } from './chats/teammates/store/teammates.actions';

/** The three route-resolved loads the blocking loading screen waits on. */
export type PreloadTarget = 'assistants' | 'chats' | 'teammates';

/**
 * Consecutive failures of a single target before the session is treated as
 * unrecoverable and cleared (SB-3521, US-4 "Repeated failures").
 */
export const PRELOAD_MAX_CONSECUTIVE_FAILURES = 2;

/**
 * Delay before an automatic retry. The loading screen keeps animating across
 * it, so the wait is invisible; retrying instantly against a hard-down API
 * would burn both attempts in the same outage and sign the user out at once.
 */
export const PRELOAD_RETRY_DELAY_MS = 1_000;

/**
 * Watches the preload the blocking loading screen sits on top of, retries a
 * failed load once, and clears the session when the same load fails twice.
 *
 * ## Why the action stream and not store selectors
 *
 * `chatsFeature`, `assistantsFeature` and `teammatesFeature` are registered in
 * the *route* providers (`app.routes.ts`), not at the root, so a root-provided
 * service cannot `select` them — the slices do not exist until the chats route,
 * or the admin `skills` route for the first two, activates. `Actions` is
 * root-provided, so it is the only place a root service can observe these loads
 * from. That is a constraint, not a preference.
 *
 * ## Only a preload navigation's failures count
 *
 * The same loads also run outside a preload: the admin `skills` page loads the
 * skills on entry, and a session that starts under `/admin` keeps the retry
 * window open until a navigation ends under `/chats`, which may never happen.
 * So a failure counts only while the router is navigating to a URL that
 * carries the preload, the one case where a resolver is waiting on the load,
 * and only when that target's load started during the same navigation. A
 * load already in flight when the navigation began can fail before the
 * resolver dispatches its own; the resolver then starts a fresh one, so that
 * failure hangs nothing and must not use up the resolver's retry. The count
 * belongs to that navigation: a new preload navigation starts with the full
 * budget, even if an earlier one was replaced before it settled.
 *
 * ## Why a failed preload hangs forever without this service
 *
 * `chatsResolver`, `assistantsResolver` and `teammatesResolver` all wait with
 * `filter((loaded) => loaded), take(1)`, and none of the failure reducers ever
 * set `loaded` — `loadChatsFailure` records only `error` (chats.reducer.ts),
 * `loadTeammatesFailure` sets `loaded: false` (teammates.reducer.ts), and
 * assistants has no failure case at all. So on a
 * failed load the resolver never emits: the navigation never completes, and
 * *neither* `NavigationEnd` nor `NavigationError` fires. Nothing reports the
 * failure, so without this service there is nothing to retry or count.
 *
 * Retrying works precisely *because* the resolver is still hanging: its
 * subscription is live, so re-dispatching the load drives `loaded` true and the
 * original navigation completes. That is why this fix needs no change to the
 * resolvers or the reducers.
 */
@Injectable({ providedIn: 'root' })
export class AppPreloadService {
  private readonly actions$ = inject(Actions);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly translateService = inject(TranslateService);

  private readonly consecutiveFailures = new Map<PreloadTarget, number>();
  /** The preload navigation `consecutiveFailures` counts for. */
  private countedNavigationId: number | null = null;
  /** The preload navigation during which each target's load last started. */
  private readonly armedNavigationIds = new Map<PreloadTarget, number>();

  private readonly _isComplete = signal(false);
  private readonly _isFirstNavigationComplete = signal(false);
  private readonly _isAbandoned = signal(false);

  /**
   * True once a navigation has settled on the route that runs the preload —
   * the point at which all three watched loads have resolved, since they are
   * that route's resolvers. This governs the retry window, nothing else.
   */
  public readonly isComplete = this._isComplete.asReadonly();

  /**
   * True once the first navigation has ended, wherever it landed.
   *
   * What the loading screen waits on, and deliberately a different signal from
   * {@link isComplete}. The screen covers one navigation and has to come down
   * when it settles, including on routes that run no preload at all — `/login`
   * for a signed-out visitor being the ordinary case. Waiting on the preload
   * there would leave it up forever.
   */
  public readonly isFirstNavigationComplete =
    this._isFirstNavigationComplete.asReadonly();

  /** True once repeated failures have cleared the session; no further retries run. */
  public readonly isAbandoned = this._isAbandoned.asReadonly();

  constructor() {
    const navigationEnds$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    );

    // What the screen waits on: one navigation, wherever it lands.
    navigationEnds$
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this._isFirstNavigationComplete.set(true));

    // What the retry window waits on, which is deliberately not the same. The
    // app can land somewhere without a preload on the way to its real
    // destination: from `/` the router redirects to `login`, and `LoginPage`
    // then navigates a signed-in visitor on to `/chats/teammates`. Closing the
    // window on that `/login` hop closed it before the preload had been
    // dispatched at all, so a load failing afterwards hung exactly as it did
    // before this service existed.
    navigationEnds$
      .pipe(
        filter((event) => carriesPreloadFor(event.urlAfterRedirects)),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.consecutiveFailures.clear();
        this._isComplete.set(true);
      });

    this.watch(
      'assistants',
      AssistantsActions.loadAssistants.type,
      AssistantsActions.loadAssistantsFailure.type,
      AssistantsActions.loadAssistantsSuccess.type,
      () => AssistantsActions.loadAssistants(),
    );
    this.watch(
      'chats',
      ChatsActions.loadChats.type,
      ChatsActions.loadChatsFailure.type,
      ChatsActions.loadChatsSuccess.type,
      () => ChatsActions.loadChats(),
    );
    this.watch(
      'teammates',
      TeammatesActions.loadTeammates.type,
      TeammatesActions.loadTeammatesFailure.type,
      TeammatesActions.loadTeammatesSuccess.type,
      () =>
        TeammatesActions.loadTeammates({
          locale: this.translateService.getCurrentLang() as SupportedLanguage,
        }),
    );
  }

  private watch(
    target: PreloadTarget,
    loadType: string,
    failureType: string,
    successType: string,
    retry: () => Action,
  ): void {
    // The resolver's own dispatch, and the retry's, arm the target for the
    // navigation in flight. For chats the effect keeps a request already in
    // flight (`exhaustMap`), and the resolver then waits on that one, so its
    // failure counts too.
    this.actions$
      .pipe(ofType(loadType), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const navigationId = this.preloadNavigationInFlight();
        if (navigationId !== null) {
          this.armedNavigationIds.set(target, navigationId);
        }
      });

    this.actions$
      .pipe(ofType(failureType), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.onFailure(target, retry));

    this.actions$
      .pipe(ofType(successType), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.consecutiveFailures.delete(target));
  }

  private onFailure(target: PreloadTarget, retry: () => Action): void {
    // These same loads are re-dispatched during normal use — `chats.effects.ts`
    // reloads chats and `chats.socket.ts` reloads teammates. Once the preload is
    // over those are ordinary background failures, and counting them would sign
    // the user out mid-session. A load no preload navigation waits on (such as
    // the admin `skills` page's, on entry) is a background failure too, even
    // while the window is open.
    const navigationId = this.preloadNavigationInFlight();
    if (this.isSettled() || navigationId === null) {
      return;
    }
    // A load that started before this navigation is not the one its resolver
    // waits on: the resolver dispatches a fresh load of its own.
    if (this.armedNavigationIds.get(target) !== navigationId) {
      return;
    }
    // A new preload navigation starts with the full budget: failures counted
    // for one that was replaced before it settled no longer count.
    if (navigationId !== this.countedNavigationId) {
      this.consecutiveFailures.clear();
      this.countedNavigationId = navigationId;
    }

    const failures = (this.consecutiveFailures.get(target) ?? 0) + 1;
    this.consecutiveFailures.set(target, failures);

    if (failures >= PRELOAD_MAX_CONSECUTIVE_FAILURES) {
      this._isAbandoned.set(true);
      // A real sign-out, not a plain redirect to `/login`. With the session left
      // intact, `LoginPage` sends a still-signed-in user straight back to
      // `/chats/teammates`, the same load fails twice again, and the app loops.
      // The known cost: `logout()` goes through the API, so when the API itself
      // is down the user lands on a connection error instead of the login page.
      this.store.dispatch(AuthActions.logout());
      return;
    }

    timer(PRELOAD_RETRY_DELAY_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.isSettled()) {
          return;
        }
        this.store.dispatch(retry());
      });
  }

  private isSettled(): boolean {
    return this._isComplete() || this._isAbandoned();
  }

  /**
   * The id of the navigation in flight when it goes to a URL that runs the
   * preload, the only time a resolver waits on these loads; `null` otherwise.
   * `finalUrl` is set once the route is recognized; `extractedUrl` covers the
   * phase before it.
   */
  private preloadNavigationInFlight(): number | null {
    const navigation = this.router.currentNavigation();
    const target = navigation?.finalUrl ?? navigation?.extractedUrl;
    return navigation &&
      target &&
      carriesPreloadFor(this.router.serializeUrl(target))
      ? navigation.id
      : null;
  }
}

/**
 * Starts {@link AppPreloadService} at bootstrap without blocking startup, so it
 * is listening before the first navigation dispatches any of the three loads.
 * Follows the pattern of providePageViewTrackingInitializer.
 */
export function provideAppPreloadInitializer(): EnvironmentProviders {
  return provideAppInitializer(() => {
    inject(AppPreloadService);
  });
}
